/* Lab 1.4 · 同一个抛掷，四个引擎

   Every pose on screen is recorded (data.json, from sims/make_data.py): one box thrown with the same
   mass, friction and initial velocity in MuJoCo, Isaac Lab (PhysX), Newton and Genesis, 60 frames a
   second for 3 s. Each engine keeps its own default contact model; the page only replays them, one
   lane each, with a white marker at the four-engine mean so every lane shows its own offset.

   The scripted take (9 s, episode 1.4 segment 26):
     0–1.4   free flight, slowed down: the four boxes move as one
     1.4–5.6 the landing at 1/8 speed: boxes touching the ground light up amber, the curves split
     5.6–7.4 the rest of the 3 s at normal speed
     7.4–9   hold: where each engine left the box */
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { C, q, recorded, createStage, floor, run, clamp, span, ease, el, toScreen } from "../kit/stage";
import { Chart } from "../kit/chart";
import { localizePage, tr, type Dict } from "../kit/i18n";
import DATA from "./data.json";
import STRINGS from "./i18n.json";

localizePage(STRINGS as Dict);

interface Engine {
  id: string; engine: string; version: string; settings: string; fps: number;
  pos: number[][]; quat: number[][]; touch: number[]; low_mm: number[]; dev_cm: number[];
  first_contact: number; max_pen_mm: number; final_dev_cm: number;
}
const ENG = DATA.engines as unknown as Engine[];
const S = DATA.spec;
const FPS = S.fps, N = ENG[0].pos.length, T_END = (N - 1) / FPS;
const FIRST = Math.min(...ENG.map(e => e.first_contact)) / FPS;
const COLOUR: Record<string, string> = { mujoco: "#34d399", isaac: "#38bdf8", newton: "#a78bfa", genesis: "#f472b6" };
const LANE = .3;
// impact moments: frames where a box goes from airborne to touching (a bounce makes a second one)
const IMPACTS = ENG.map(e => e.touch.flatMap((v, j) => v && !e.touch[j - 1] ? [j / FPS] : []));
const cm = (v: number): string => String(Math.round(v * 1000) / 10);                                   // lanes along world y, first engine furthest back

// --- the scripted take: take time -> simulation time
function simTime(t: number): number {
  if (t < 1.4) return ease(span(t, .3, 1.4)) * .2;
  if (t < 5.6) return .2 + span(t, 1.4, 5.6) * .52;            // 0.2 → 0.72 s at 1/8 speed
  return .72 + ease(span(t, 5.6, 7.4)) * (T_END - .72);
}

// --- scene
const canvas = el<HTMLCanvasElement>("stage");
const { scene, camera, render } = createStage(canvas);
camera.setViewOffset(1920, 1080, -330, 70, 1920, 1080);   // the lanes sit right of the chart panel
floor(scene);
const W = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, z, -y);   // z up -> three.js y up
const Q = (w: number, x: number, y: number, z: number): THREE.Quaternion => new THREE.Quaternion(x, z, -y, w);
const laneY = (k: number): number => (1.5 - k) * LANE;
const [hx, hy, hz] = S.half;

interface Lane { box: THREE.Mesh; edges: THREE.LineSegments; trail: THREE.Line; mark: THREE.Mesh; tie: THREE.Line; shadowRing: THREE.Mesh; label: HTMLElement }
const lanes: Lane[] = ENG.map((e, k) => {
  const col = new THREE.Color(COLOUR[e.id] ?? "#e6edf3");
  const box = new THREE.Mesh(new THREE.BoxGeometry(2 * hx, 2 * hz, 2 * hy),
    new THREE.MeshStandardMaterial({ color: col, emissive: col, emissiveIntensity: .25, metalness: .2, roughness: .45 }));
  box.castShadow = true;
  const edges = new THREE.LineSegments(new THREE.EdgesGeometry(box.geometry), new THREE.LineBasicMaterial({ color: col }));
  box.add(edges);
  const trail = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: .55 }));
  const mark = new THREE.Mesh(new THREE.SphereGeometry(.007, 16, 12), new THREE.MeshBasicMaterial({ color: 0xe6edf3 }));
  const tie = new THREE.Line(new THREE.BufferGeometry(), new THREE.LineBasicMaterial({ color: 0xe6edf3, transparent: true, opacity: .7 }));
  const shadowRing = new THREE.Mesh(new THREE.RingGeometry(.07, .082, 48), new THREE.MeshBasicMaterial({ color: C.amber, transparent: true, opacity: 0, depthWrite: false }));
  shadowRing.rotation.x = -Math.PI / 2;
  // the lane: a faint strip on the floor
  const strip = new THREE.Mesh(new THREE.PlaneGeometry(.9, LANE * .82), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: .035, depthWrite: false }));
  strip.rotation.x = -Math.PI / 2;
  strip.position.copy(W(.25, laneY(k), .001));
  scene.add(box, trail, mark, tie, shadowRing, strip);
  const label = document.createElement("div");
  label.className = "lbl";
  label.style.color = COLOUR[e.id];
  el("labels").appendChild(label);
  return { box, edges, trail, mark, tie, shadowRing, label };
});

// --- controls
const controls = new OrbitControls(camera, canvas);
const TARGET = W(.2, 0, .02);
controls.target.copy(TARGET);
Object.assign(controls, { enableDamping: true, minDistance: .5, maxDistance: 3, maxPolarAngle: 1.45, enabled: !recorded });
camera.position.copy(W(.2, -1.75, 1.3));
const user = { t: 0, play: true };
const tIn = el<HTMLInputElement>("t");
tIn.addEventListener("input", () => { user.t = Number(tIn.value); user.play = false; el<HTMLInputElement>("play").checked = false; });
el<HTMLInputElement>("play").addEventListener("change", e => { user.play = (e.target as HTMLInputElement).checked; });
let last = performance.now();

// --- chart: distance from the mean, per engine, first 1.2 s (after that nothing moves)
const X_MAX = 1.2;
const yMax = Math.ceil(Math.max(...ENG.map(e => Math.max(...e.dev_cm))) + .5);
const chart = new Chart(el("chart"), { x: [0, X_MAX], y: [0, yMax], xTicks: [0, .2, .4, .6, .8, 1, 1.2], yTicks: [0, 1, 2, 3, 4, 5].filter(v => v <= yMax),
  xFormat: v => `${v.toFixed(1)} s`, yFormat: v => `${v} cm` }, 700, 250, { l: 58, r: 26, t: 12, b: 34 });
const upto = (a: number[], i: number): [number, number][] => a.slice(0, i + 1).map((v, j) => [j / FPS, v] as [number, number]).filter(p => p[0] <= X_MAX);

el("rows").innerHTML = `<span></span><span class="h" style="text-align:left">${tr("引擎")}</span><span class="h">${tr("最深穿透")}</span><span class="h">${tr("离平均")}</span>` +
  ENG.map(e => `<i style="background:${COLOUR[e.id]}"></i><span class="name">${e.engine}<small>${e.version.split(" (")[0].replace(/^Isaac Lab /, "Lab ")}</small></span>` +
    `<span class="v" id="pen-${e.id}">—</span><span class="v" id="dev-${e.id}">—</span>`).join("");
el("note").textContent = tr("盒子 {0}×{1}×{2} cm、{3} g、摩擦 {4}，从 {5} cm 高斜着抛出。", cm(2 * hx), cm(2 * hy), cm(2 * hz), Math.round(S.mass * 1000), S.mu, cm(S.pos0[2])) +
  tr("每个引擎用自己的默认接触设置。落地前四者相差不到 {0} cm（只是积分步长不同）；", Math.ceil(Math.max(...ENG.map(e => e.dev_cm[e.first_contact - 1])) * 10) / 10) +
  tr("停下时最远的两个相差 {0} cm。穿透：盒角压进地面的深度，“软”接触会先压进去再弹回。", DATA.final_max_pair_cm);
// the episode take shows only the key difference, large; the full footnote stays on the live page
el("key").textContent = tr("停下时最远的两个相差 {0} cm", DATA.final_max_pair_cm);
el("src").textContent = tr("数据：本机实跑 · ") + ENG.map(e => `${e.engine} ${e.version.replace(/ \(.*?\)/, "")}`).join(" · ") + " · sims/*.py";

function frameAt(t: number): void {
  let st: number;
  if (recorded) st = simTime(t);
  else {
    const now = performance.now();
    if (user.play) user.t = (user.t + (now - last) / 1000 * .25) % (T_END + .5);
    last = now;
    st = Math.min(user.t, T_END);
    tIn.value = String(st);
    el("tVal").textContent = `${st.toFixed(2)} s`;
  }
  const i = clamp(Math.round(st * FPS), 0, N - 1);
  const m = DATA.mean[i];
  const contactPhase = st >= FIRST - .02 && st < .72;

  ENG.forEach((e, k) => {
    const L = lanes[k], dy = laneY(k), p = e.pos[i], qq = e.quat[i];
    L.box.position.copy(W(p[0], p[1] + dy, p[2]));
    L.box.quaternion.copy(Q(qq[0], qq[1], qq[2], qq[3]));
    // glow: each impact flashes amber and fades over 0.15 s of simulation time
    const glowK = Math.max(0, ...IMPACTS[k].filter(a => a <= st + 1e-9).map(a => Math.exp(-(st - a) / .15)));
    const touching = glowK > .15;
    const amber = new THREE.Color(C.amber);
    const mat = L.box.material as THREE.MeshStandardMaterial;
    const base = new THREE.Color(COLOUR[e.id]);
    mat.emissive.copy(base.clone().lerp(amber, glowK));
    mat.emissiveIntensity = .25 + .75 * glowK;
    (L.edges.material as THREE.LineBasicMaterial).color.copy(base.clone().lerp(amber, glowK));
    L.shadowRing.position.copy(W(p[0], p[1] + dy, .002));
    (L.shadowRing.material as THREE.MeshBasicMaterial).opacity = .9 * glowK;
    L.shadowRing.scale.setScalar(1 + .5 * (1 - glowK));
    L.trail.geometry.setFromPoints(e.pos.slice(0, i + 1).map(v => W(v[0], v[1] + dy, v[2])));
    L.mark.position.copy(W(m[0], m[1] + dy, m[2]));
    L.tie.geometry.setFromPoints([L.mark.position, L.box.position]);
    // labels: engine name over the lane; while the box touches, its penetration so far
    const pen = Math.max(0, -Math.min(...e.low_mm.slice(0, i + 1)));
    const at = toScreen(W(p[0], p[1] + dy, Math.max(p[2], .03) + .07), camera, canvas);
    L.label.style.left = `${at.x}px`;
    L.label.style.top = `${at.y}px`;
    L.label.classList.toggle("hot", touching);
    L.label.innerHTML = `${e.engine}<small>${touching ? tr("撞地 · 压进 {0} mm", pen.toFixed(1)) : i >= e.first_contact ? tr("偏 {0} cm", e.dev_cm[i].toFixed(1)) : ""}</small>`;
    el("pen-" + e.id).textContent = i >= e.first_contact ? `${pen.toFixed(1)} mm` : "—";
    el("pen-" + e.id).classList.toggle("hot", touching && pen > .5);
    el("dev-" + e.id).textContent = `${e.dev_cm[i].toFixed(1)} cm`;
  });

  chart.draw({
    bands: [{ lo: [[FIRST, 0], [.72, 0]], hi: [[FIRST, yMax], [.72, yMax]], colour: "#fbbf24", opacity: contactPhase ? .12 : .05 }],
    series: ENG.map(e => ({ points: upto(e.dev_cm, i), colour: COLOUR[e.id], width: 2.4 })),
    cursor: Math.min(st, X_MAX),
  });
  el("phase").innerHTML = st < FIRST - .02 ? tr("空中 · 慢放") : st < .72 ? `<span style="color:var(--l3)">${tr("落地 · 放慢 8 倍")}</span>` : i < N - 1 ? tr("滑行、停下") : tr("停下");
  if (!recorded) el("phase").textContent = `${st.toFixed(2)} s`;

  if (recorded) {
    const az = -1.62 + t * .01, r = 1.75;
    camera.position.set(TARGET.x + Math.cos(az) * r, 1.3, TARGET.z - Math.sin(az) * r);
    camera.lookAt(TARGET);
  } else controls.update();
  render();
  el("clock").textContent = "T+" + t.toFixed(2).padStart(5, "0");
  el("dot").style.opacity = (t * 2 | 0) % 2 ? ".3" : "1";
}

run(frameAt, { dur: Number(q.get("dur") || 9), still: 3.2 });
